import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Checkbox,
} from '@mui/material';
import type { Theme } from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
  SwapHoriz as SwapIcon,
  Timer as TimerIcon,
  Delete as DeleteIcon,
  EditNote as BulkEditIcon,
} from '@mui/icons-material';
import { hardwareAPI, type Logger, type LoggerLocation } from '../services';
import { useAuth } from '../context/AuthContext';
import { useMapView } from '../context/MapViewContext';
import { isSuperAdmin, hasPermission } from '../utils/userPermissions';
import EmptyState from '../components/EmptyState';
import LicenceTypeManagerDialog from '../components/hardware/LicenceTypeManagerDialog';
import BulkEditDialog from '../components/hardware/BulkEditDialog';
import {
  HEADER_CELL_SX,
  ListToolbar,
  ListMap,
  ToneDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  type MapPin,
  type RowAction,
} from '../components/ListPageShared';
import {
  statusMeta,
  utcToLocalString,
  HardwareImage,
  batteryInfo,
  signalInfo,
  lastSeen,
  formatRelative,
  frequencyLabel,
  formatDate,
  formatDateOnly,
  displayValue,
  assignmentInfo,
  modeLabel,
  isConxagent,
  hasLoggingFrequency,
} from '../components/hardware/hardwareDisplay';
import { ChangeFrequencyDialog, ChangeModeDialog } from '../components/hardware/DeviceActionDialogs';
import type { DeviceActionResult } from '../services';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

// This is a module-level pure function (no useTheme access), so the edge
// colour is resolved separately at each render site from `theme.palette` —
// keeps every status colour on one source of truth (App.tsx theme).
const edgeColorFor = (theme: Theme, colorKey: 'success' | 'warning' | 'error' | 'default'): string => {
  switch (colorKey) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    default:
      return theme.palette.grey[400];
  }
};

type StatusFilterKey = 'All' | 'UP' | 'SUSPECT' | 'DOWN';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Hardware: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loggers, setLoggers] = useState<Logger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('All');
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const selection = useRowSelection();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Delete confirmation dialog state. `deleteTarget` holds the logger row,
  // `deleting` blocks the button while the API call is in flight.
  const [deleteTarget, setDeleteTarget] = useState<Logger | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { user } = useAuth();
  const canManage = hasPermission(user, 'hardware.edit_advanced');
  const canAdd = hasPermission(user, 'hardware.add');
  const canLocate = hasPermission(user, 'hardware.edit_basic');
  const canViewDetails = hasPermission(user, 'hardware.view_details');
  const [locating, setLocating] = useState<string | null>(null);
  // Row actions: ConXagent mode change, logging frequency change
  const [modeTarget, setModeTarget] = useState<Logger | null>(null);
  const [freqTarget, setFreqTarget] = useState<Logger | null>(null);

  const requestLocation = async (loggerId: string) => {
    setLocating(loggerId);
    try {
      const result = await hardwareAPI.requestLocation(loggerId);
      setSnackbar({ msg: result.message, severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        msg: err.response?.data?.detail || t('hardware.locateFailed', 'Location request failed'),
        severity: 'error',
      });
    } finally {
      setLocating(null);
    }
  };
  const userIsSuperAdmin = isSuperAdmin(user);
  const [licenceDialogOpen, setLicenceDialogOpen] = useState(false);

  // Bulk edit (super admin only). Selection is kept across pages and
  // filters, keyed by LoggerID, with the row so the dialog can list it.
  const canBulkEdit = userIsSuperAdmin && canManage;
  const [selected, setSelected] = useState<Map<string, Logger>>(new Map());
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleSelected = (logger: Logger) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(logger.LoggerID)) next.delete(logger.LoggerID);
      else next.set(logger.LoggerID, logger);
      return next;
    });

  // Server-side pagination state: the fleet is ~7k+ devices, far beyond the
  // API's 100-per-request cap, so every filter/search/count is a server query.
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ All: 0, UP: 0, SUSPECT: 0, DOWN: 0 });
  const [locations, setLocations] = useState<LoggerLocation[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchFilter.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchFilter]);

  const serverParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      logger_type: typeFilter !== 'All' ? typeFilter : undefined,
    }),
    [debouncedSearch, typeFilter],
  );

  const fetchLoggers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await hardwareAPI.getLoggers({
        ...serverParams,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        page: page + 1,
        size: rowsPerPage,
      });
      setLoggers(response.loggers || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch hardware data');
      console.error('Error fetching loggers:', err);
    } finally {
      setLoading(false);
    }
  }, [serverParams, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchLoggers();
  }, [fetchLoggers]);

  // Fleet-wide status counts for the status switch (respect search/type
  // filters, not the status filter itself).
  useEffect(() => {
    hardwareAPI
      .getLoggerStatusSummary(serverParams)
      .then((summary) =>
        setStatusCounts({
          All: summary.total ?? 0,
          UP: summary.UP ?? 0,
          SUSPECT: summary.SUSPECT ?? 0,
          DOWN: summary.DOWN ?? 0,
        }),
      )
      .catch((err) => console.error('Error fetching status summary:', err));
  }, [serverParams]);

  // Loggers with their own reported position, for the map. Refreshed with
  // the list so a new fix shows up on the next refresh click.
  useEffect(() => {
    hardwareAPI
      .getLoggerLocations()
      .then((rows) => setLocations(rows || []))
      .catch((err) => console.error('Error fetching logger locations:', err));
  }, [loggers]);

  // On mobile the map is opened from the bottom-nav Map button (shared context)
  const mapView = useMapView();

  // Type dropdown options come from the server (display names across the
  // whole fleet, not just the loaded page).
  useEffect(() => {
    hardwareAPI
      .getLoggerTypes()
      .then(setAvailableTypes)
      .catch((err) => console.error('Error fetching logger types:', err));
  }, []);

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, typeFilter, statusFilter]);

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await hardwareAPI.deleteLogger(deleteTarget.LoggerID);
      if (result.temptrack_unregistered === false) {
        // Row is soft-deleted but the device is still registered on
        // TempTrack — warn so it can be cleaned up upstream.
        setSnackbar({
          msg: t(
            'hardware.deletedTemptrackFailed',
            'Hardware deleted, but TempTrack unregister failed — device may still be registered upstream',
          ),
          severity: 'error',
        });
      } else {
        setSnackbar({ msg: t('hardware.deleted', 'Hardware deleted'), severity: 'success' });
      }
      setDeleteTarget(null);
      fetchLoggers();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || 'Delete failed',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const renderBattery = (logger: Logger) => {
    const info = batteryInfo(logger.BatteryLevel as unknown as string);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: info.color }}>
        {info.icon}
        <Typography variant="body2" sx={{ color: info.pct === null ? 'text.disabled' : 'text.primary' }}>
          {info.pct === null ? '—' : `${info.pct}%`}
        </Typography>
      </Box>
    );
  };

  const renderSignal = (logger: Logger) => {
    const info = signalInfo(logger.SignalStrengh as unknown as string);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: info.color }}>
        {info.icon}
        <Typography variant="body2" noWrap sx={{ color: info.label ? 'text.primary' : 'text.disabled' }}>
          {info.label || '—'}
        </Typography>
      </Box>
    );
  };

  const renderActivity = (logger: Logger) => {
    const seen = formatRelative(lastSeen(logger));
    const freq = frequencyLabel(logger.LoggerFrequency);
    return (
      <Box>
        <Typography variant="body2" noWrap sx={{ color: seen ? 'text.primary' : 'text.disabled' }}>
          {seen || t('hardware.noActivity', 'No recent data')}
        </Typography>
        {freq && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {freq}
          </Typography>
        )}
      </Box>
    );
  };

  // Client column: the buyer or hirer, or HDTECH with what it's available for.
  const clientInfo = (logger: Logger): {
    primary: string; muted: boolean; secondary?: string; alert?: boolean;
  } => {
    const info = assignmentInfo(logger, t);
    return { primary: info.company, muted: false, secondary: info.status, alert: info.color === 'error' };
  };

  const onDeviceAction = (result: DeviceActionResult) => {
    setModeTarget(null);
    setFreqTarget(null);
    setSnackbar({ msg: result.message, severity: 'success' });
    // The new frequency is stored straight away; mode updates on check-in
    fetchLoggers();
  };

  const pageSelectedCount = loggers.filter((l) => selected.has(l.LoggerID)).length;
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageSelectedCount === loggers.length) loggers.forEach((l) => next.delete(l.LoggerID));
      else loggers.forEach((l) => next.set(l.LoggerID, l));
      return next;
    });

  const onBulkApplied = (updatedIds: string[]) => {
    // Saved devices leave the selection; failed ones stay for another try
    setSelected((prev) => {
      const next = new Map(prev);
      updatedIds.forEach((id) => next.delete(id));
      return next;
    });
    fetchLoggers();
  };

  const statusDot = (logger: Logger) => {
    const status = statusMeta(logger.TrackStatus);
    return <ToneDot color={edgeColorFor(theme, status.color)} label={status.label} />;
  };

  const actionsFor = (logger: Logger) => {
    // One device page for viewing and editing; it needs
    // hardware.view_details, and is read-only without edit rights.
    const open: RowAction | false = canViewDetails && {
      key: 'open',
      label: canManage ? t('common.edit', 'Edit') : t('common.open', 'Open'),
      icon: canManage ? <EditIcon fontSize="small" /> : <OpenIcon fontSize="small" />,
      onClick: () => navigate(`/hardware/${logger.LoggerID}`),
    };
    const mode: RowAction | false = canManage && isConxagent(logger) && {
      key: 'mode',
      label: t('hardware.changeMode', 'Change mode'),
      icon: <SwapIcon fontSize="small" />,
      onClick: () => setModeTarget(logger),
    };
    const frequency: RowAction | false = canLocate && hasLoggingFrequency(logger) && {
      key: 'frequency',
      label: t('hardware.changeFrequency', 'Change frequency'),
      icon: <TimerIcon fontSize="small" />,
      onClick: () => setFreqTarget(logger),
    };
    const remove: RowAction | false = canManage && {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(logger),
    };
    return { menu: [open, mode, frequency, remove], strip: [open, mode, frequency] };
  };

  const detailStrip = (logger: Logger) => {
    const calibration = logger.CalibrationEffectiveDate
      ? `${formatDate(logger.CalibrationEffectiveDate)}${
          logger.CalibrationFrequencyNumber
            ? ` (every ${logger.CalibrationFrequencyNumber} ${logger.CalibrationFrequencyUnit || 'months'})`
            : ''
        }`
      : '—';
    return (
      <DetailStrip
        fields={[
          { label: t('hardware.purpose', 'Purpose'), value: displayValue(logger.SaleOrHire) },
          {
            label: t('hardware.hirePeriod', 'Hire'),
            value:
              logger.Assignment === 'ON_HIRE' &&
              `${formatDateOnly(logger.HireStartDate)} to ${logger.ReturnDate ? formatDateOnly(logger.ReturnDate) : t('hardware.openEnded', 'open')}`,
          },
          { label: t('hardware.connection', 'Connection'), value: displayValue(logger.ConnectionType) },
          { label: t('hardware.mode', 'Mode'), value: modeLabel(logger.Mode) },
          { label: t('hardware.firmware', 'Firmware'), value: displayValue(logger.Firmware) },
          { label: t('hardware.lastCalibration', 'Last Calibration'), value: calibration },
        ]}
        description={
          logger.Note ? (
            <Box component="span" sx={{ fontStyle: 'italic' }}>{logger.Note}</Box>
          ) : (
            t('hardware.moreOnDevicePage', 'Licence, SIM, gateway and channel details are on the device page.')
          )
        }
        actions={actionsFor(logger).strip}
      />
    );
  };

  // Map pins: every logger that has reported its own fix, coloured by status
  const pins: MapPin[] = useMemo(
    () =>
      locations.map((loc) => ({
        id: loc.LoggerID,
        position: [loc.Latitude, loc.Longitude] as [number, number],
        color: edgeColorFor(theme, statusMeta(loc.TrackStatus).color),
        popup: (
          <>
            <strong>{loc.DeviceName || loc.SerialNumber || loc.LoggerID}</strong>
            <br />
            {loc.DeviceName && loc.SerialNumber && <>{loc.SerialNumber}<br /></>}
            {t('hardware.locationMethod', 'Located by')}: {loc.LocationMethod || '-'}
            {loc.LocationAccuracy != null && <> (&plusmn;{loc.LocationAccuracy} m)</>}
            <br />
            {loc.LocationTime && (
              <>
                {t('hardware.locationTime', 'Reported')}: {utcToLocalString(loc.LocationTime)}
                <br />
              </>
            )}
            <Button
              size="small"
              sx={{ mt: 1, textTransform: 'none', p: 0 }}
              onClick={() => navigate(`/hardware/${loc.LoggerID}`)}
            >
              {t('hardware.viewHardware', 'View hardware')}
            </Button>
            {canLocate && (
              <>
                <br />
                <Button
                  size="small"
                  sx={{ textTransform: 'none', p: 0 }}
                  disabled={locating === loc.LoggerID}
                  onClick={() => requestLocation(loc.LoggerID)}
                >
                  {t('hardware.requestLocation', 'Request location now')}
                </Button>
              </>
            )}
          </>
        ),
      })),
    // requestLocation only reads state through its argument
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations, theme, navigate, t, canLocate, locating],
  );

  /** A pin click selects its row when that device is on the current page. */
  const selectFromMap = (id: string) => {
    if (loggers.some((l) => l.LoggerID === id)) selection.selectAndReveal(id);
  };

  const emptyState = (
    <EmptyState
      title={t('hardware.noHardware')}
      description={t('hardware.noHardwareDescription')}
      icon={<SearchIcon />}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 25, 50, 100],
    component: 'div' as const,
    count: total,
    rowsPerPage,
    page,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (loading && loggers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const columnCount = canBulkEdit ? 10 : 9;

  const filterSummary =
    searchFilter || typeFilter !== 'All' || statusFilter !== 'All'
      ? `Showing ${total} of ${statusCounts.All} devices (filtered)`
      : `Total devices: ${total}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchFilter}
        onSearch={setSearchFilter}
        searchPlaceholder={t('hardware.searchPlaceholder')}
        statusOptions={[
          { key: 'All', label: t('hardware.allDevices', 'All Devices'), count: statusCounts.All },
          { key: 'UP', label: t('hardware.online', 'Online'), count: statusCounts.UP },
          { key: 'SUSPECT', label: t('hardware.suspect', 'Suspect'), count: statusCounts.SUSPECT },
          { key: 'DOWN', label: t('hardware.offline', 'Offline'), count: statusCounts.DOWN },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={fetchLoggers}
        loading={loading}
        addLabel={canAdd ? t('hardware.addHardware', 'Add Hardware') : undefined}
        onAdd={() => navigate('/hardware/new')}
        filters={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="type-filter-label">{t('hardware.type')}</InputLabel>
            <Select
              labelId="type-filter-label"
              value={typeFilter}
              label={t('hardware.type')}
              onChange={handleTypeFilterChange}
            >
              <MenuItem value="All">{t('hardware.allTypes')}</MenuItem>
              {availableTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        extraActions={
          userIsSuperAdmin && (
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setLicenceDialogOpen(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {t('hardware.licenceTypes', 'Licence Types')}
            </Button>
          )
        }
      />

      {/* Logger locations map: only devices that have reported their own fix */}
      <ListMap
        pins={pins}
        selectedId={selection.selectedId}
        onSelect={selectFromMap}
        label={t('hardware.locatedCount', '{{count}} on map', { count: pins.length })}
        emptyOverlay={t('hardware.noLocations', 'No loggers have reported a location yet')}
        pinColor={theme.palette.grey[500]}
        selectedPinColor={theme.palette.grey[800]}
        isMobile={isMobile}
        mobileOpen={mapView.open}
      />

      <LicenceTypeManagerDialog
        open={licenceDialogOpen}
        onClose={() => {
          setLicenceDialogOpen(false);
          // Licence names shown in expanded rows may have changed
          fetchLoggers();
        }}
      />

      {canBulkEdit && (
        <BulkEditDialog
          open={bulkOpen}
          devices={Array.from(selected.values())}
          onClose={() => setBulkOpen(false)}
          onApplied={onBulkApplied}
        />
      )}

      <ChangeModeDialog logger={modeTarget} onClose={() => setModeTarget(null)} onDone={onDeviceAction} />
      <ChangeFrequencyDialog logger={freqTarget} onClose={() => setFreqTarget(null)} onDone={onDeviceAction} />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {loggers.length === 0 ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            loggers.map((logger) => (
              <SelectableCard
                key={logger.LoggerID}
                rowRef={selection.rowRef(logger.LoggerID)}
                selected={selection.selectedId === logger.LoggerID}
                onClick={() => selection.toggle(logger.LoggerID)}
                header={
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}><HardwareImage typeName={logger.TypeName} size={28} /></Box>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {logger.SerialNumber}
                      </Typography>
                    </Box>
                    {statusDot(logger)}
                    <RowActionsMenu actions={actionsFor(logger).menu} />
                  </>
                }
                body={
                  <>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[logger.TypeName, clientInfo(logger).primary].filter((v) => v && v !== 'Unknown').join(' · ') || '—'}
                      {logger.HireOverdue && (
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                          {' · '}{t('hardware.overdue', 'Overdue')}
                        </Box>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                      {renderBattery(logger)}
                      {renderSignal(logger)}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {formatRelative(lastSeen(logger)) || ''}
                      </Typography>
                    </Box>
                  </>
                }
                detail={detailStrip(logger)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <>
        {canBulkEdit && selected.size > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              borderColor: 'primary.main', bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('hardware.selectedCount', '{{n}} selected', { n: selected.size })}
            </Typography>
            {selected.size > pageSelectedCount && (
              <Typography variant="caption" color="text.secondary">
                {t('hardware.selectedOtherPages', 'including {{n}} on other pages or filters', { n: selected.size - pageSelectedCount })}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelected(new Map())} sx={{ textTransform: 'none' }}>
              {t('hardware.clearSelection', 'Clear selection')}
            </Button>
            <Button
              size="small" variant="contained" startIcon={<BulkEditIcon />}
              onClick={() => setBulkOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              {t('hardware.editSelected', 'Edit selected')}
            </Button>
          </Paper>
        )}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {canBulkEdit && (
                  <TableCell padding="checkbox" sx={HEADER_CELL_SX}>
                    <Checkbox
                      size="small"
                      checked={loggers.length > 0 && pageSelectedCount === loggers.length}
                      indeterminate={pageSelectedCount > 0 && pageSelectedCount < loggers.length}
                      onChange={togglePage}
                      disabled={loggers.length === 0}
                      inputProps={{ 'aria-label': t('hardware.selectPage', 'Select all on this page') }}
                    />
                  </TableCell>
                )}
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.device', 'Device')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.company', 'Client')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.currentPour', 'Pour')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.licence', 'Licence')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.battery', 'Battery')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.signal', 'Signal')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('hardware.activity', 'Activity')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 100 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loggers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                loggers.map((logger) => (
                  <SelectableTableRow
                    key={logger.LoggerID}
                    rowRef={selection.rowRef(logger.LoggerID)}
                    selected={selection.selectedId === logger.LoggerID}
                    onClick={() => selection.toggle(logger.LoggerID)}
                    colSpan={columnCount}
                    detail={detailStrip(logger)}
                  >
                    {canBulkEdit && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selected.has(logger.LoggerID)}
                          onChange={() => toggleSelected(logger)}
                          inputProps={{ 'aria-label': t('hardware.selectDevice', 'Select {{name}}', { name: logger.HMTechNumber || logger.SerialNumber }) }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                          <HardwareImage typeName={logger.TypeName} size={36} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {logger.SerialNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {[
                              logger.TypeName !== 'Unknown' ? logger.TypeName : null,
                              logger.Mode && !['NONE', 'None'].includes(logger.Mode) ? `${logger.Mode.charAt(0)}${logger.Mode.slice(1).toLowerCase()} mode` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const info = clientInfo(logger);
                        return (
                          <>
                            <Typography variant="body2" noWrap sx={{ color: info.muted ? 'text.disabled' : 'text.primary' }}>
                              {info.primary}
                            </Typography>
                            {info.secondary && (
                              <Typography
                                variant="caption"
                                noWrap
                                display="block"
                                sx={{ color: info.alert ? 'error.main' : 'text.secondary', fontWeight: info.alert ? 600 : undefined }}
                              >
                                {info.secondary}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {logger.CurrentPourCode ? (
                        <Chip
                          label={logger.CurrentPourCode}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          {t('hardware.idle', 'Idle')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ color: logger.LicenceTypeName ? 'text.primary' : 'text.disabled', maxWidth: 180 }}
                      >
                        {logger.LicenceTypeName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderBattery(logger)}</TableCell>
                    <TableCell>{renderSignal(logger)}</TableCell>
                    <TableCell>{renderActivity(logger)}</TableCell>
                    <TableCell>{statusDot(logger)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(logger).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
              {typeFilter !== 'All' && <span> &bull; {t('hardware.type')}: {typeFilter}</span>}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
        </>
      )}


      {/* Delete confirmation dialog. Modal so the user can't dismiss it
          while the API call is in-flight. */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('hardware.deleteConfirmTitle', 'Delete hardware?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('hardware.deleteConfirmBody', {
              defaultValue:
                'Delete hardware {{label}}? This cannot be undone.',
              label: deleteTarget?.HMTechNumber || deleteTarget?.SerialNumber || deleteTarget?.LoggerID,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting
              ? t('hardware.deleting', 'Deleting...')
              : t('hardware.confirmDelete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hardware;
